package com.elan.edubridge.shared.domain.model

import com.elan.edubridge.shared.domain.policy.PermissionPolicy
import kotlinx.serialization.Serializable

@Serializable
data class SessionState(
    val account: UserAccount? = null,
    val memberships: List<AcademyMembership> = emptyList(),
    val activeAcademyId: String? = null,
) {
    val isAuthenticated: Boolean get() = account != null

    val usableMemberships: List<AcademyMembership>
        get() = memberships.filter { it.isUsable }

    val activeMembership: AcademyMembership?
        get() = usableMemberships.firstOrNull { it.academyId == activeAcademyId }
            ?: usableMemberships.firstOrNull()

    /** 활성 멤버십 role → 전역 SUPER_ADMIN → 계정 기본 role 순으로 해석한다. */
    val activeRole: UserRole
        get() {
            val current = account ?: return UserRole.STUDENT
            activeMembership?.let { return it.role }
            if (UserRole.SUPER_ADMIN in current.globalRoles) return UserRole.SUPER_ADMIN
            return current.baseRole
        }

    /**
     * 화면 분기·인가 판정이 보는 유일한 축(§2.2, §5.2).
     *
     * 멤버십에 저장된 값을 그대로 쓴다. 역할에서 다시 파생하면 장래 예외
     * (부원장 = TEACHER 역할 + ADMIN 권한)를 흡수하지 못하고 정책 구현이 2벌이 된다.
     * 멤버십이 없는 계정만 역할에서 파생한다.
     */
    val activePermission: Permission
        get() {
            val current = account ?: return Permission.VIEWER
            activeMembership?.let { return it.permission }
            if (UserRole.SUPER_ADMIN in current.globalRoles) return Permission.ADMIN
            return PermissionPolicy.permissionFor(current.baseRole)
        }

    /** 선생님 출결 읽기 범위(§4.5). 비어 있으면 배정 반이 없다는 뜻이다. */
    val activeAssignedClassIds: List<String>
        get() = activeMembership?.assignedClassIds.orEmpty()

    /** 역할 전환 UI가 보여줄 후보. */
    val availableRoles: Set<UserRole>
        get() = buildSet {
            account?.let { add(it.baseRole); addAll(it.globalRoles) }
            addAll(usableMemberships.map { it.role })
        }

    val requiresGuardianConsent: Boolean
        get() = account?.consentStatus == GuardianConsentStatus.PENDING_GUARDIAN_CONSENT
}
